import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { FlaskConical, Plus, Download, Target, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PHASES = ["Pilot", "Pivotal", "PMCF", "Registry"];

export default function Studies() {
  const [list, setList] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [detail, setDetail] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [form, setForm] = useState({ protocol_id: "", title: "", phase: "Pilot", indication: "", endpoints: [{ name: "", kind: "primary", description: "" }] });
  const navigate = useNavigate();

  const load = async () => setList((await api.get("/studies")).data);
  useEffect(() => { load(); }, []);

  const openDetail = async (sid) => {
    const [s, c] = await Promise.all([
      api.get(`/studies/${sid}`), api.get(`/studies/${sid}/completeness`),
    ]);
    setDetail(s.data); setCompleteness(c.data);
  };

  const create = async () => {
    if (!form.protocol_id.trim() || !form.title.trim()) return toast.error("Protocol ID and title required");
    try {
      const clean = { ...form, endpoints: form.endpoints.filter((e) => e.name.trim()) };
      await api.post("/studies", clean);
      toast.success("Study created");
      setOpenNew(false);
      setForm({ protocol_id: "", title: "", phase: "Pilot", indication: "", endpoints: [{ name: "", kind: "primary", description: "" }] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const exportCsv = async (sid) => {
    const res = await api.get(`/studies/${sid}/export/csv`, { responseType: "blob" });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${sid}_export.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportSdtm = async (sid) => {
    const res = await api.get(`/studies/${sid}/export/sdtm`);
    const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${sid}_sdtm.json`; a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="vq-overline mb-2">Clinical</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Studies</h1>
            <p className="text-slate-600 mt-2 max-w-3xl">Protocol registry, endpoints, subject rosters, and exports.</p>
          </div>
          <Button onClick={() => setOpenNew(true)} data-testid="study-new-btn"
                  className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> New Study
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 vq-card overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 vq-overline">All Studies ({list.length})</div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {list.map((s) => (
                <button key={s.study_id} data-testid={`study-row-${s.study_id}`}
                        onClick={() => openDetail(s.study_id)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${detail?.study_id === s.study_id ? "bg-slate-100" : ""}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <FlaskConical className="w-3.5 h-3.5 text-[#0055FF]" />
                    <span className="text-[9px] font-mono uppercase bg-slate-100 text-slate-700 rounded px-1">{s.phase}</span>
                    <span className="text-[10px] font-mono text-slate-400">{s.protocol_id}</span>
                  </div>
                  <div className="font-semibold text-sm text-slate-900 truncate">{s.title}</div>
                  <div className="text-[10px] font-mono text-slate-400">{(s.endpoints || []).length} endpoint(s)</div>
                </button>
              ))}
              {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No studies yet.</div>}
            </div>
          </div>

          <div className="lg:col-span-3">
            {detail ? (
              <>
                <div className="vq-card p-6 mb-4">
                  <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                    <div>
                      <div className="vq-overline font-mono">{detail.protocol_id} · {detail.phase}</div>
                      <h2 className="font-display font-bold text-xl text-slate-900 mt-1">{detail.title}</h2>
                      {detail.indication && <div className="text-sm text-slate-600 mt-1">{detail.indication}</div>}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => exportCsv(detail.study_id)} data-testid="export-csv-btn"
                              className="border-slate-900 text-slate-900"><Download className="w-3 h-3 mr-1" /> CSV</Button>
                      <Button size="sm" variant="outline" onClick={() => exportSdtm(detail.study_id)} data-testid="export-sdtm-btn"
                              className="border-[#0055FF] text-[#0055FF]"><Download className="w-3 h-3 mr-1" /> SDTM</Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <MetricCard label="Forms" value={detail.form_count} />
                    <MetricCard label="Subjects" value={detail.subject_count} />
                    <MetricCard label="Entries" value={detail.entry_count} />
                  </div>
                  <Button size="sm" onClick={() => navigate(`/clinical/forms?study_id=${detail.study_id}`)}
                          data-testid="open-forms-btn"
                          className="bg-slate-900 hover:bg-[#0055FF] text-white">
                    Open Forms <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>

                {completeness && (
                  <div className="vq-card p-6">
                    <div className="flex items-center gap-2 mb-3"><Target className="w-5 h-5 text-[#0055FF]" />
                      <div className="font-display font-bold text-lg text-slate-900">Endpoint completeness</div></div>
                    {completeness.warning && (
                      <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-3">{completeness.warning}</div>
                    )}
                    <div className="space-y-2">
                      {completeness.endpoints.map((ep) => (
                        <div key={ep.endpoint_id} className="border border-slate-200 rounded p-3">
                          <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                            <div>
                              <span className={`text-[9px] font-mono uppercase mr-2 px-1 rounded ${ep.kind === "primary" ? "bg-[#0055FF] text-white" : ep.kind === "safety" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700"}`}>{ep.kind}</span>
                              <span className="font-semibold text-sm text-slate-900">{ep.name}</span>
                            </div>
                            <span className={`font-mono text-sm font-bold ${ep.completeness_pct >= 80 ? "text-emerald-700" : ep.completeness_pct >= 50 ? "text-amber-700" : "text-red-700"}`}>{ep.completeness_pct}%</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded overflow-hidden">
                            <div className={`h-full ${ep.completeness_pct >= 80 ? "bg-emerald-500" : ep.completeness_pct >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                                 style={{ width: `${ep.completeness_pct}%` }} />
                          </div>
                          <div className="text-[10px] font-mono text-slate-400 mt-1">{ep.filled_cells} / {ep.total_cells} cells across {ep.field_count} field(s)</div>
                        </div>
                      ))}
                      {completeness.endpoints.length === 0 && <div className="text-sm text-slate-500">No endpoints defined for this study.</div>}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="vq-card p-10 text-center text-slate-500">Select a study.</div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">New Study</DialogTitle>
            <DialogDescription>Endpoints defined here anchor every eCRF field (Principle 7).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Protocol ID</Label>
                <Input value={form.protocol_id} data-testid="study-form-protocol"
                       onChange={(e) => setForm({ ...form, protocol_id: e.target.value })}
                       className="mt-1.5" placeholder="P-001" /></div>
              <div><Label className="text-xs font-semibold">Phase</Label>
                <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="study-form-phase"><SelectValue /></SelectTrigger>
                  <SelectContent>{PHASES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs font-semibold">Title</Label>
              <Input value={form.title} data-testid="study-form-title"
                     onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Indication (optional)</Label>
              <Input value={form.indication} onChange={(e) => setForm({ ...form, indication: e.target.value })} className="mt-1.5" /></div>
            <div>
              <Label className="text-xs font-semibold">Endpoints</Label>
              <div className="space-y-2 mt-1.5">
                {form.endpoints.map((ep, i) => (
                  <div key={`ep-${i}`} className="grid grid-cols-6 gap-2">
                    <Input className="col-span-3" placeholder="Name" value={ep.name}
                           data-testid={`study-form-endpoint-name-${i}`}
                           onChange={(e) => { const arr = [...form.endpoints]; arr[i].name = e.target.value; setForm({ ...form, endpoints: arr }); }} />
                    <Select value={ep.kind} onValueChange={(v) => { const arr = [...form.endpoints]; arr[i].kind = v; setForm({ ...form, endpoints: arr }); }}>
                      <SelectTrigger className="col-span-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="primary">Primary</SelectItem>
                        <SelectItem value="secondary">Secondary</SelectItem>
                        <SelectItem value="safety">Safety</SelectItem>
                        <SelectItem value="exploratory">Exploratory</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" onClick={() => setForm({ ...form, endpoints: form.endpoints.filter((_, j) => j !== i) })}>×</Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setForm({ ...form, endpoints: [...form.endpoints, { name: "", kind: "secondary", description: "" }] })}>
                  + Add endpoint
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
            <Button onClick={create} data-testid="study-form-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="border border-slate-200 rounded p-3">
      <div className="vq-overline text-[9px]">{label}</div>
      <div className="font-display font-bold text-2xl text-slate-900 mt-1">{value ?? 0}</div>
    </div>
  );
}
